"use client";

import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";

import AdminConfirmationModal from "../AdminConfirmationModal/AdminConfirmationModal";
import usePosts from "../../../hooks/usePosts";
import {
  selectEffectiveUser,
  selectEffectiveUserId,
} from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./CreatePostBox.css";
import Cookies from "js-cookie";

/* ==================== Utils recadrage ==================== */
async function getCroppedDataURL(imageSrc, cropPixels, rotation = 0) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });

  const rads = (rotation * Math.PI) / 180;
  const safeW = Math.ceil(
    Math.abs(image.width * Math.cos(rads)) + Math.abs(image.height * Math.sin(rads))
  );
  const safeH = Math.ceil(
    Math.abs(image.width * Math.sin(rads)) + Math.abs(image.height * Math.cos(rads))
  );

  const canvas = document.createElement("canvas");
  canvas.width = safeW;
  canvas.height = safeH;
  const ctx = canvas.getContext("2d");

  ctx.translate(safeW / 2, safeH / 2);
  ctx.rotate(rads);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  ctx.rotate(-rads);
  ctx.translate(-safeW / 2, -safeH / 2);

  const { x, y, width, height } = cropPixels;
  const data = ctx.getImageData(x, y, width, height);

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const outCtx = out.getContext("2d");
  outCtx.putImageData(data, 0, 0);

  return out.toDataURL("image/jpeg", 0.92);
}
function dataURLtoFile(dataurl, filename = "cropped.jpg") {
  const [head, body] = dataurl.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "image/jpeg";
  const bin = atob(body);
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new File([u8], filename, { type: mime });
}

/* ==================== Composant ==================== */
export default function CreatePostBox({ onPostCreated }) {
  // ↑ onPostCreated : callback optionnel pour notifier le parent
  //   qu'un nouveau post vient d'être créé (lui passer le post complet)

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [postContent, setPostContent] = useState("");
  const [photoPreviews, setPhotoPreviews] = useState([]); // DataURL
  const [files, setFiles] = useState([]); // File[]
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [moderationMessage, setModerationMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [aspect, setAspect] = useState(4 / 5);

  const { handleCreatePost, createLoading } = usePosts();
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const user = useSelector(selectEffectiveUser);

  /* ================= Handle Multiple Images Upload ================= */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const validFiles = [];
    const newPreviews = [];

    selectedFiles.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        alert(t("posts.errors.invalidImage", { name: file.name }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(t("posts.errors.imageTooLarge", { name: file.name }));
        return;
      }

      validFiles.push(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === validFiles.length) {
          setPhotoPreviews((prev) => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const openEditor = (index) => {
    setEditingIndex(index);
    setEditorOpen(true);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
    setAspect(4 / 5);
  };

  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const applyCrop = async () => {
    if (editingIndex == null || !croppedPixels) {
      setEditorOpen(false);
      return;
    }
    try {
      const src = photoPreviews[editingIndex];
      const dataUrl = await getCroppedDataURL(src, croppedPixels, rotation);
      setPhotoPreviews((prev) => prev.map((p, i) => (i === editingIndex ? dataUrl : p)));
      setFiles((prev) =>
        prev.map((f, i) => (i === editingIndex ? dataURLtoFile(dataUrl, f?.name || "cropped.jpg") : f))
      );
    } finally {
      setEditorOpen(false);
      setEditingIndex(null);
    }
  };

  /* ================= FIX: handlePublish avec date locale ================= */
  const handlePublish = async () => {
    if (!postContent.trim()) {
      alert(t("posts.errors.emptyContent"));
      return;
    }

    const userId = effectiveUserId || JSON.parse(Cookies.get("user") || "{}")?.id;
    if (!userId) {
      alert(t("posts.errors.userNotIdentified"));
      return;
    }

    // ✅ FIX: on capture l'heure exacte de publication AVANT l'appel API
    const localNow = new Date().toISOString();

    let postData;
    if (files.length > 0) {
      postData = new FormData();
      postData.append("description", postContent.trim());
      postData.append("userId", userId);
      postData.append("isAnonymous", isAnonymous);
      files.forEach((file) => postData.append("file", file));
    } else {
      postData = { description: postContent.trim(), userId, photo: null, isAnonymous };
    }

    try {
      const result = await handleCreatePost(postData);

      if (result?.success !== true) {
        const message = result?.message || t("posts.moderation.refused");
        setModerationMessage(`❌ ${message}`);
        setShowConfirmationModal(true);
      } else {
        // ✅ FIX: construire le post optimiste avec la date locale exacte
        // L'API retourne parfois une date UTC décalée ou null → on force localNow
        const returnedPost = result?.post ?? result?.data ?? result ?? null;

        const optimisticPost = {
          // Champs de l'API si disponibles
          ...(returnedPost && typeof returnedPost === "object" ? returnedPost : {}),
          // Champs garantis côté client
          description: postContent.trim(),
          userId,
          isAnonymous,
          // ✅ Date forcée au moment exact de la publication
          createdAt: returnedPost?.createdAt ?? returnedPost?.created_at ?? localNow,
          created_at: returnedPost?.createdAt ?? returnedPost?.created_at ?? localNow,
          // ID temporaire si l'API ne retourne pas d'ID immédiatement
          id_post: returnedPost?.id_post ?? returnedPost?.postId ?? returnedPost?.id ?? `temp_${Date.now()}`,
          postId: returnedPost?.postId ?? returnedPost?.id_post ?? returnedPost?.id ?? `temp_${Date.now()}`,
          // User embedé pour affichage immédiat sans fetch Redux
          user: returnedPost?.user ?? null,
          role: returnedPost?.role ?? 'patient',
        };

        // ✅ FIX: notifier le parent pour qu'il insère le post en tête de liste
        // sans attendre un re-fetch complet
        if (typeof onPostCreated === "function") {
          onPostCreated(optimisticPost);
        }

        // Reset du formulaire
        setPostContent("");
        setFiles([]);
        setPhotoPreviews([]);
        setIsAnonymous(false);
      }
    } catch (error) {
      setModerationMessage(error?.message || t("posts.errors.createFailed"));
      setShowConfirmationModal(true);
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  return (
    <>
      <div className="post-box-container">
        {/* Header */}
        <div className="post-box-header">
          <span className="post-box-title">{t("posts.headerTitle")}</span>

          <button
            className="post-box-anonymous-toggle"
            type="button"
            onClick={() => setIsAnonymous((prev) => !prev)}
          >
            {t("posts.askAnonymously")}

            <div className="post-box-toggle-switch">
              <input
                type="checkbox"
                id="anonymousToggle"
                checked={isAnonymous}
                onChange={() => setIsAnonymous((prev) => !prev)}
              />
              <label htmlFor="anonymousToggle" />
            </div>
          </button>
        </div>

        {/* Input area */}
        <div className="post-box-body">
          <div className="post-box-input-row">
            <div className="post-box-input-wrapper">
              <input
                type="text"
                className="post-box-question-input"
                placeholder={t("posts.placeholder")}
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                disabled={createLoading}
              />

              <input
                type="file"
                accept="image/*"
                id="fileInput"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={createLoading}
                multiple
              />

              <button
                className="post-box-attach-button"
                onClick={() => document.getElementById("fileInput")?.click()}
                disabled={createLoading}
                title={t("posts.attachImage")}
                type="button"
              >
                📎
              </button>
            </div>

            <button
              className={`post-box-publish-btn ${postContent.trim() ? "active" : ""}`}
              disabled={!postContent.trim() || createLoading}
              onClick={handlePublish}
              type="button"
            >
              {createLoading ? t("posts.publishing") : t("posts.publishBtn")}
            </button>
          </div>

          {/* Preview Images */}
          {photoPreviews.length > 0 && (
            <div className="post-box-preview-images-container">
              {photoPreviews.map((preview, index) => (
                <div key={index} className="post-box-preview-image">
                  <img
                    src={preview}
                    alt={t("posts.previewAlt", { index: index + 1 })}
                    className="post-box-preview-img"
                  />

                  <div className="post-box-preview-actions">
                    <button
                      className="post-box-edit-photo-btn"
                      type="button"
                      onClick={() => openEditor(index)}
                      disabled={createLoading}
                    >
                      ✎ {t("com.edit")}
                    </button>

                    <button
                      className="post-box-remove-photo-btn"
                      onClick={() => removePhoto(index)}
                      disabled={createLoading}
                      type="button"
                      aria-label={t("com.remove")}
                      title={t("com.remove")}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <AdminConfirmationModal message={moderationMessage} onClose={handleCloseConfirmationModal} />
      )}

      {/* === CROP MODAL === */}
      {editorOpen && editingIndex != null && (
        <div className="post-box-cropper-backdrop" role="dialog" aria-modal="true">
          <div className="post-box-cropper-modal">
            <div className="post-box-cropper-header">
              <strong>{t("posts.crop.title")}</strong>
              <button
                className="post-box-cropper-close"
                onClick={() => setEditorOpen(false)}
                type="button"
                aria-label={t("com.close")}
                title={t("com.close")}
              >
                ✕
              </button>
            </div>

            <div className="post-box-cropper-area">
              <Cropper
                image={photoPreviews[editingIndex]}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                restrictPosition={false}
              />
            </div>

            <div className="post-box-cropper-controls">
              <label className="post-box-ctrl">
                {t("posts.crop.zoom")}
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </label>

              <div className="post-box-aspect-group">
                <span>{t("posts.crop.aspect")}</span>
                <button
                  className={`post-box-asp ${aspect === 1 ? "active" : ""}`}
                  onClick={() => setAspect(1)}
                  type="button"
                >
                  1:1
                </button>
                <button
                  className={`post-box-asp ${aspect === 4 / 5 ? "active" : ""}`}
                  onClick={() => setAspect(4 / 5)}
                  type="button"
                >
                  4:5
                </button>
                <button
                  className={`post-box-asp ${aspect === 16 / 9 ? "active" : ""}`}
                  onClick={() => setAspect(16 / 9)}
                  type="button"
                >
                  16:9
                </button>
              </div>
            </div>

            <div className="post-box-cropper-actions">
              <button className="post-box-btn-secondary" type="button" onClick={() => setEditorOpen(false)}>
                {t("com.cancel")}
              </button>
              <button className="post-box-btn-primary" type="button" onClick={applyCrop}>
                {t("com.apply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
